// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'
import { toAdminListParams } from '@/core/lib/table-query'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'
import { userRoleListQueryOptions } from '@/modules/user-role/user-role.hooks'

// Module Imports
import {
  playerDetailQueryOptions,
  playerListQueryOptions,
} from '@/modules/player/player.hooks'
import { playerSearchSchema } from '@/modules/player/player.schemas'
import type { TPlayer } from '@/modules/player/player.types'

export const playerRoute = createRoute({
  path: '/players',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const playerListRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '/',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'players.list',
      'Oyuncu listesine erişim izniniz yok.'
    )
  },
  validateSearch: playerSearchSchema,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    await queryClient.ensureQueryData(
      playerListQueryOptions(toAdminListParams(deps))
    )
  },
  head: () => ({
    meta: [
      {
        title: 'Oyuncu Listesi',
      },
    ],
  }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/player/pages/list-player.page')
  ),
})

export const playerDetailRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '$id',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'players.detail',
      'Oyuncu detayına erişim izniniz yok.'
    )
  },
  head: ({ params: { id } }) => {
    const player = queryClient.getQueryData<TPlayer>(['playerDetail', id])

    return {
      meta: [
        {
          title: `Oyuncu | ${player?.name ?? id}`,
        },
      ],
    }
  },
  loader: async ({ params: { id } }) => {
    await queryClient.ensureQueryData(playerDetailQueryOptions(id))
  },
  component: lazyRouteComponent(
    () => import('@/modules/player/pages/player-detail.page')
  ),
})

export const playerEditRoute = createRoute({
  getParentRoute: () => playerRoute,
  path: '$id/edit',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'users.roles.assign',
      'Oyuncu rol atama izniniz yok.'
    )
  },
  head: () => ({
    meta: [
      {
        title: 'Oyuncu Rolünü Düzenle',
      },
    ],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(playerDetailQueryOptions(id)),
      queryClient.ensureQueryData(userRoleListQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/player/pages/edit-player.page')
  ),
})
